import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { CalendarService, ICalendar } from '../calendar.service';
import { IFileDataSchema } from '../file-transfer-services/interfaces';
import { JsonFileTransfer, jsonImportError } from '../file-transfer-services/jsonFileTransfer.service';
import { ITeamProfile, ITeamProfileConfiguration, ITimeInterval, IWeekDays, IWorkDay, TeamProfileService } from '../teamProfiles.service';
import { MesTeamProfileValidationService } from '../Validation/mesTeamProfileValidation.service';
import { FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

export interface IMesTeamProfileExportData {
	alias: string;
	description: string;
	calendarAlias: string;
	timeZoneId: string;
	configurations: Array<ITeamProfileConfiguration>;
}

const MesTimeIntervalSchema: IFileDataSchema<ITimeInterval> = {
	name: 'TimeInterval',
	properties: {
		startTime: { type: 'string', required: true },
		endTime: { type: 'string', required: true },
	},
};

const MesWorkDaySchema: IFileDataSchema<IWorkDay> = {
	name: 'WorkDay',
	properties: {
		isWorkDay: { type: 'boolean', required: true },
		workTime: { type: { schema: MesTimeIntervalSchema }, required: false },
		variableWeeklyWorkTime: { type: { schema: MesTimeIntervalSchema }, required: false },
		breaks: { type: { array: { schema: MesTimeIntervalSchema } }, required: false },
	},
};

const MesWeekDaysSchema: IFileDataSchema<IWeekDays> = {
	name: 'WeekDays',
	properties: {
		monday: { type: { schema: MesWorkDaySchema }, required: false },
		tuesday: { type: { schema: MesWorkDaySchema }, required: false },
		wednesday: { type: { schema: MesWorkDaySchema }, required: false },
		thursday: { type: { schema: MesWorkDaySchema }, required: false },
		friday: { type: { schema: MesWorkDaySchema }, required: false },
		saturday: { type: { schema: MesWorkDaySchema }, required: false },
		sunday: { type: { schema: MesWorkDaySchema }, required: false },
	},
};

const MesTeamProfileConfigurationSchema: IFileDataSchema<ITeamProfileConfiguration> = {
	name: 'TeamProfileConfiguration',
	properties: {
		alias: { type: 'string', required: true },
		activeDate: { type: 'string', required: true },
		weekDays: { type: { schema: MesWeekDaysSchema }, required: false },
		roundingTime: { type: 'number', required: false },
		endRoundingTime: { type: 'number', required: false },
	},
};

const MesTeamProfileSchema: IFileDataSchema<IMesTeamProfileExportData> = {
	name: 'TeamProfile',
	properties: {
		alias: { type: 'string', required: true },
		description: { type: 'string', required: false },
		calendarAlias: { type: 'string', required: false },
		timeZoneId: { type: 'string', required: false },
		configurations: {
			type: { array: { schema: MesTeamProfileConfigurationSchema } },
			required: false,
		},
	},
};

export interface MesTeamProfileImportContext {
	teamProfiles: ITeamProfile[];
	calendars: ICalendar[];
}

@Injectable({
	providedIn: 'root',
})
export class MesTeamProfileTransferService implements ITransferService<ITeamProfile> {
	constructor(
		private teamProfileService: TeamProfileService,
		private calendarService: CalendarService,
		private mesTeamProfileImportContext: MesTeamProfileValidationService,
		private jsonFileTransfer: JsonFileTransfer<IMesTeamProfileExportData>,
		private translator: TranslateService,
	) {}

	// Team profiles transfer as JSON, which has no template analogue — answered honestly rather
	// than by omission, and getTemplate throws so a UI ignoring hasTemplate() fails loudly.
	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('Team profiles transfer as JSON and offer no import template; check hasTemplate() before calling getTemplate().');
	};

	public exportData(user: ITeamProfile[]) {
		return from(user).pipe(
			combineAllWith(this.calendarService.getCalendars().pipe(map((calendars) => calendars.result))),
			map(([profile, calendars]) => {
				const calendar = calendars.find((c) => c.id === profile.calendarId);

				return {
					alias: profile.alias,
					description: profile.description,
					calendarAlias: calendar?.alias,
					timeZoneId: profile.timeZoneId,
					configurations: profile.configurations,
				};
			}),
			toArray(),
			mergeMap((data) => {
				return this.jsonFileTransfer.exportData('TeamProfiles', data, MesTeamProfileSchema);
			}),
			map(() => user), // Return the original user array after export
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	importData(file: File | File[], overwrite: boolean = false) {
		const ctx: MesTeamProfileImportContext = {
			teamProfiles: [],
			calendars: [],
		};

		const ImportErrors: ImportErrorTypes[] = [];

		if (!Array.isArray(file)) {
			// If a single file is provided, we convert it to an array
			file = [file];
		}

		const ob = readFiles(file, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				forkJoin([this.teamProfileService.getTeamProfiles(), this.calendarService.getCalendars()]).pipe(
					map(([teamProfiles, calendars]) => {
						ctx.teamProfiles = teamProfiles.result;
						ctx.calendars = calendars.result;
						return {};
					}),
				),
			),
			map(([user]) => user),
			filterMap(this.validateAndMapCalendar(ctx, ImportErrors)),
			filterMap(this.validateTeamProfile(ImportErrors)),
			mergeMap(this.createOrUpdateTeamProfile(ctx, overwrite, ImportErrors)),
			toArray(),
			map((users) => {
				const successfulImports = users.map((u) => ({ Id: u.entity.alias, status: u.status }) as SuccessFullImport);

				return {
					successfulImports,
					errors: ImportErrors,
				} as ImportResponse;
			}),
		);

		return ob;
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IMesTeamProfileExportData> => {
			const errorList: jsonImportError[] = [];

			try {
				const rows = this.jsonFileTransfer.importData(buffer, MesTeamProfileSchema, errorList).map((imported) => imported.data);

				if (errorList.length > 0) {
					// If there are errors, we throw an error with the list of errors
					errorList.forEach((e) => {
						ImportErrors.push({
							message: e.error,
							status: ImportStatus.ParsingError,
						} as FailedParse);
					});
				}

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	validateAndMapCalendar(ctx: MesTeamProfileImportContext, ImportErrors: ImportErrorTypes[]) {
		return (teamProfile: IMesTeamProfileExportData): ITeamProfile | undefined => {
			const calendar = ctx.calendars.find((c) => c.alias === teamProfile.calendarAlias);
			if (!calendar) {
				const message = this.translator.instant('mes.administration.team-profiles.import.errors.calendar-not-found', { calendar: teamProfile.calendarAlias, alias: teamProfile.alias });
				ImportErrors.push({
					Id: `(${teamProfile.alias}) ${teamProfile.description}`,
					status: ImportStatus.Failed,
					message,
				});
				return undefined;
			}

			return {
				alias: teamProfile.alias,
				description: teamProfile.description,
				calendarId: calendar.id,
				timeZoneId: teamProfile.timeZoneId,
				configurations: teamProfile.configurations ?? [],
			} as ITeamProfile;
		};
	}

	validateTeamProfile(ImportErrors: ImportErrorTypes[]) {
		return (teamProfile: ITeamProfile): ITeamProfile | undefined => {
			// Validate task
			const validationErrors = this.mesTeamProfileImportContext.validateTeamProfile(teamProfile, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: `(${teamProfile.alias}) ${teamProfile.description}`,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return teamProfile;
		};
	}

	createOrUpdateTeamProfile(ctx: MesTeamProfileImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (task: ITeamProfile) => {
			const existingTask = ctx.teamProfiles.find((e) => e.alias === task.alias);
			if (existingTask) {
				if (!overwrite) {
					const message = this.translator.instant('mes.administration.team-profiles.import.errors.exists', { alias: task.alias });
					// Do not overwrite existing task
					ImportErrors.push({
						Id: `(${task.alias}) ${task.description}`,
						status: ImportStatus.Skipped,
						message,
					});
					return of();
				}
				// preserve the id of the existing task
				task.id = existingTask.id;
				// Update existing task
				return this.teamProfileService.updateTeamProfile(task).pipe(
					map(() => ({
						entity: task,
						status: ImportStatus.SuccessUpdate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.team-profiles.import.errors.update-failed', { alias: task.alias, msg: error.message });
						ImportErrors.push({
							Id: `(${task.alias}) ${task.description}`,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			} else {
				// Create new task
				return this.teamProfileService.createTeamProfile(task).pipe(
					map(() => ({
						entity: task,
						status: ImportStatus.SuccessCreate,
					})),

					catchError((error) => {
						const message = this.translator.instant('mes.administration.team-profiles.import.errors.create-failed', { alias: task.alias, msg: error.message });
						ImportErrors.push({
							Id: `(${task.alias}) ${task.description}`,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			}
		};
	}
}
